import { LocationMapDialog } from '@/components/controls';
import { getGroupType, spin } from '@/utils';
import { timePeriods } from '@/utils/config';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { CrimeAgainstStaff } from './CrimeAgainstStaff';
import { useNicheCrimesAgainstStaff } from './useNicheCrimesAgainstStaff';

export function CrimesAgainstStaff({ title, groupCodes, onComplete }) {
  const [days, setDays] = useState(1);
  const {
    query: { data: crimes, isFetching, refetch },
  } = useNicheCrimesAgainstStaff(getGroupType('SECTOR'), groupCodes, days);
  const [selectedId, setSelectedId] = useState(null);
  const { width, ref } = useResizeDetector();
  const columns = useMemo(() => Math.floor(width / 248) || 1, [width]);
  const selectedInstance = useMemo(
    () => crimes.find((instance) => instance.occurrence.number === selectedId),
    [crimes, selectedId],
  );

  function handleRefreshClick() {
    refetch();
  }

  function handleCompleteClick() {
    onComplete();
  }

  function handleDaysChange(event, value) {
    setDays(+value);
  }

  const handleShowMapClick = (id) => () => {
    setSelectedId(id);
  };

  function handleMapClose() {
    setSelectedId(null);
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup
          value={days.toString()}
          onChange={handleDaysChange}
          color="primary"
          size="small"
          exclusive
        >
          {Object.entries(timePeriods).map(([value, label]) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton onClick={handleRefreshClick} disabled={isFetching}>
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        <Masonry spacing={1} columns={columns}>
          {crimes.map((crime) => (
            <CrimeAgainstStaff
              key={crime.occurrence.number}
              crime={crime}
              onShowMapClick={handleShowMapClick(crime.occurrence.number)}
            />
          ))}
        </Masonry>
      </Stack>
      {selectedInstance?.occurrence?.address && (
        <LocationMapDialog
          addresses={[selectedInstance.occurrence.address]}
          title={`Points of interest for ${selectedInstance.occurrence.number}`}
          isOpen={Boolean(selectedInstance)}
          onClose={handleMapClose}
        />
      )}
    </Box>
  );
}
